import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal, Popover2, ButtonTooltip } from '@evertrue/et-components';
import EverTrue from 'app';
import CadenceDetailBreadcrumb from '../detail/cadence-detail-breadcrumb';
import CadenceDetailHeader from '../detail/cadence-detail-header';
import CadenceRecipientsTable from '../detail/cadence-recipients-table';
import CadencePerformanceChart from '../detail/cadence-performance-chart';
import ContactPicker from 'apps/contact/components/contact-picker';
import DuplicateCadenceModal from '../duplicate/duplicate-cadence-modal';
import CadenceSharingModal from '../sharing/cadence-sharing-modal';
import ConfirmModal from '../shared/confirm-modal';
import CadenceAudienceSources from '../detail/cadence-audience-sources';
import useCadence from '../../hooks/use-cadence';
import useCadenceStats from '../../hooks/use-cadence-stats';
import useCadenceMetricProgress from '../../hooks/use-cadence-metric-progress';
import useCadenceMutations from '../../hooks/use-cadence-mutations';
import useDetailPageActions from '../../hooks/use-detail-page-actions';
import { useCadenceJobs, useJobPoller } from '../../hooks/use-job-poller';
import { METRIC_TYPE_LABELS, getRoundedPercentage } from '../../utils/cadence-data-mappers';
import { getCancelSendBlockReason, buildCancelSendConfirmBody } from '../../utils/cadence-cancel-send';

/**
 * Bulk Message detail page showing performance metrics and recipients.
 * Fetches real data from the GraphQL backend via useCadence and useCadenceStats hooks.
 */
const BulkMessageDetailPage = ({ messageId }) => {
  const { cadence: message, loading, error, refetch } = useCadence(messageId);
  const {
    stats,
    stepStats,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useCadenceStats(messageId);
  const { metrics: metricProgressList, refetch: refetchMetricProgress } = useCadenceMetricProgress(messageId);

  // Check for in-progress enrollment jobs (bulk enroll, list import, or saved search import)
  const { jobs: enrollJobs, refetch: refetchJobs } = useCadenceJobs(messageId);
  const activeJob = enrollJobs.find((j) => !['COMPLETED', 'FAILED'].includes(j.status));
  const [pollingJobId, setPollingJobId] = useState(null);
  const { job: polledJob, isComplete: jobComplete, isFailed: jobFailed } = useJobPoller(pollingJobId);

  // Start polling when we detect an active job
  useEffect(() => {
    if (activeJob && !pollingJobId) {
      setPollingJobId(activeJob.id);
    }
  }, [activeJob, pollingJobId]);

  // When job finishes, refresh data
  useEffect(() => {
    if (jobComplete || jobFailed) {
      setPollingJobId(null);
      refetchJobs();
      refetch();
      refetchStats();
    }
  }, [jobComplete, jobFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  const enrollmentInProgress = pollingJobId && polledJob && !jobComplete && !jobFailed;

  // Toggle fade indicator when stats card row is scrolled to the end
  const handleStatsScroll = useCallback((e) => {
    const el = e.currentTarget;
    const wrap = el.parentElement;
    if (!wrap) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    wrap.classList.toggle('is-scrolled-end', atEnd);
  }, []);

  const actions = useDetailPageActions({
    entityId: messageId,
    entity: message,
    rawEntity: message,
    refetch,
    entityType: 'bulk_message',
  });

  const {
    recalculateCadenceStats,
    reevaluateSuccessMetrics,
    refreshCadenceSavedSearches,
    deleteCadenceAudience,
    revertCadenceToDraft,
  } = useCadenceMutations();
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const [membersRefreshCount, setMembersRefreshCount] = useState(0);

  const handleRefreshMetrics = async () => {
    setRefreshingMetrics(true);
    try {
      // Fire-and-trust: kick off the saved-search refresh in parallel with the
      // metric recompute. The contacts service takes a few minutes to settle
      // tag membership after the refresh signal is received, so the metric
      // values rendered immediately may not yet reflect the refreshed tags.
      // Re-clicking Refresh Metrics in a few minutes picks up the change.
      await Promise.all([
        recalculateCadenceStats(messageId),
        reevaluateSuccessMetrics(messageId),
        refreshCadenceSavedSearches(messageId).catch((err) => {
          console.warn('Saved-search refresh signal failed:', err);
        }),
      ]);
      await refetch();
      await refetchStats();
      refetchMetricProgress();
      EverTrue.Alert.success('Metrics refreshed');
    } catch (err) {
      EverTrue.Alert.error(`Failed to refresh metrics: ${err.message}`);
    } finally {
      setRefreshingMetrics(false);
    }
  };

  // Derive from canView/canEdit/canDelete — same pattern as cadence-detail-page.js.
  // Backend availableActions is unreliable (often empty); compute from permission flags instead.
  // Share and recipient management are truly owner-only — canDelete can be granted via sharing.
  const currentUserId = EverTrue.store?.user?.get('id');
  const isCadenceOwner = currentUserId != null && String(message?.ownerUserId) === String(currentUserId);
  const availableActions = (() => {
    const actions = [];
    // Viewer and above: non-destructive read actions
    if (message?.canView) actions.push('duplicate', 'save_as_blueprint');
    // Editor and above: content editing
    // cancel_send mirrors the backend gate on revertCadenceToDraft, which is
    // user_can_edit_cadence (owner, or shared with can_edit/can_delete).
    if (message?.canEdit) actions.push('edit', 'cancel_send');
    // canDelete: archive and delete (can be granted via sharing)
    if (message?.canDelete) actions.push('archive', 'delete');
    // Cadence owner only: send, recipient management, sharing
    if (isCadenceOwner) actions.push('schedule_send', 'enroll_members', 'remove_members', 'share');
    return actions;
  })();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCancelSendModal, setShowCancelSendModal] = useState(false);

  // Why this bulk message can't have its send cancelled, or null if it can.
  // Computed at render, so a message whose send time passes while the page sits
  // open can still show the item enabled — the backend re-checks and rejects
  // with a message, which is why the handler surfaces err.message.
  const cancelSendBlockReason = getCancelSendBlockReason(message);

  const handleCancelSend = async () => {
    try {
      await revertCadenceToDraft(messageId, message?.version);
      EverTrue.Alert.success('Send cancelled. This bulk message is back in draft.');
      refetch();
      refetchStats();
    } catch (err) {
      EverTrue.Alert.error(`Cancel Send failed: ${err.message}`);
    }
  };

  const handleBack = () => {
    EverTrue.Navigator('/cadences-v2?tab=bulk', true);
  };

  const breadcrumbs = [
    { label: 'Cadences', onClick: handleBack },
    { label: 'Bulk Messages', onClick: handleBack },
  ];

  if (loading) {
    return (
      <div className="bulk-message-detail-page">
        <div className="bulk-message-detail-page--loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="bulk-message-detail-page">
        <div className="bulk-message-detail-page--error">
          <Icon icon="error-outline" size={2} />
          <h3>{error ? 'Failed to load bulk message' : 'Bulk message not found'}</h3>
          {error && <p>{error}</p>}
          <Button type="secondary" onClick={handleBack}>
            Back to Bulk Messages
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-message-detail-page">
      <CadenceDetailBreadcrumb
        crumbs={breadcrumbs}
        currentName={message.name}
        className="cadence-detail-page--breadcrumb"
      />

      <div className="bulk-message-detail-page--content">
        <div className="cadence-detail-page--header-card">
          <CadenceDetailHeader
            name={message.name}
            status={message.displayStatusLabel}
            isEditingName={actions.isEditingName}
            editName={actions.editName}
            setEditName={actions.setEditName}
            onSubmitRename={availableActions.includes('edit') ? actions.handleSubmitRename : undefined}
            onCancelRename={availableActions.includes('edit') ? actions.handleCancelRename : undefined}
            onStartRename={availableActions.includes('edit') ? actions.handleStartRename : undefined}
            actions={
              <>
                <Button
                  type="secondary"
                  onClick={() => EverTrue.Navigator(`/cadences-v2/bulk-message/${messageId}/edit`, true)}
                >
                  Review Bulk Message Setup
                </Button>
                {availableActions.includes('share') && (
                  <Button type="secondary" onClick={() => setShowShareModal(true)}>
                    <Icon icon="share" />
                    <span>Share</span>
                  </Button>
                )}
                {availableActions.includes('duplicate') && (
                  <Button type="secondary" onClick={actions.handleDuplicate}>
                    Duplicate
                  </Button>
                )}
                <Popover2
                  trigger={({ togglePopover }) => (
                    <Button type="secondary" onClick={togglePopover} className="bulk-message-overflow-btn">
                      <Icon icon="more-vert" />
                    </Button>
                  )}
                >
                  {({ closePopover }) => (
                    <div className="bulk-message-overflow-menu">
                      {availableActions.includes('cancel_send') && (
                        <button
                          type="button"
                          className="bulk-message-overflow-menu--item"
                          disabled={!!cancelSendBlockReason}
                          onClick={() => {
                            closePopover();
                            setShowCancelSendModal(true);
                          }}
                        >
                          <Icon icon="cancel" />
                          <span className="bulk-message-overflow-menu--item-text">
                            Cancel Send
                            {cancelSendBlockReason && (
                              <span className="bulk-message-overflow-menu--item-reason">{cancelSendBlockReason}</span>
                            )}
                          </span>
                        </button>
                      )}
                      {availableActions.includes('enroll_members') && (
                        <button
                          type="button"
                          className="bulk-message-overflow-menu--item"
                          onClick={() => {
                            closePopover();
                            actions.setShowContactPicker(true);
                          }}
                        >
                          <Icon icon="group-add" />
                          Add Recipients
                        </button>
                      )}
                      {availableActions.includes('save_as_blueprint') && (
                        <button
                          type="button"
                          className="bulk-message-overflow-menu--item"
                          onClick={() => {
                            closePopover();
                            actions.handleShowShellInput();
                          }}
                        >
                          <Icon icon="note-add" />
                          Save as Blueprint
                        </button>
                      )}
                    </div>
                  )}
                </Popover2>
              </>
            }
          />

          {/* Primary Success Metric */}
          {message.successMetrics?.length > 0 &&
            (() => {
              const primary = message.successMetrics.find((m) => m.priority === 0) || message.successMetrics[0];
              const metricKey = (primary.metricType || '').toLowerCase();
              const metricLabel = METRIC_TYPE_LABELS[metricKey] || primary.metricType;
              return (
                <div className="bulk-message-detail-page--success-metric">
                  <Icon icon="adjust" />
                  <span className="bulk-message-detail-page--success-metric-label">Success Metric:</span>
                  <span className="bulk-message-detail-page--success-metric-value">
                    {metricLabel}
                    {primary.targetPercentage ? ` — ${primary.targetPercentage}%` : ''}
                  </span>
                </div>
              );
            })()}

          {/* Goal Progress Bar */}
          {metricProgressList.length > 0 && (
            <div className="cadence-detail-page--metrics-section">
              {metricProgressList.map((metric) => {
                const currentPct = metric.currentPercentage ?? 0;
                const targetPct = metric.targetPercentage ?? 0;
                const pctOfTarget = targetPct > 0 ? getRoundedPercentage(currentPct, targetPct) : 0;
                const barWidth = targetPct > 0 ? Math.min((currentPct / targetPct) * 100, 100) : 0;
                return (
                  <div key={metric.id} className="cadence-detail-page--metric-row">
                    <div className="cadence-detail-page--goal-header">
                      <span className="cadence-detail-page--goal-text">
                        Goal: {METRIC_TYPE_LABELS[metric.metricType] || metric.metricType}
                      </span>
                    </div>
                    <div className="cadence-detail-page--goal-bar">
                      <div className="cadence-detail-page--goal-track">
                        <div
                          className="cadence-detail-page--goal-fill bulk-message-detail-page--goal-fill"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className="cadence-detail-page--goal-pct">{currentPct}%</span>
                    </div>
                    <div className="cadence-detail-page--goal-footer">
                      <span className="cadence-detail-page--goal-progress">{pctOfTarget}% of target</span>
                      <span className="cadence-detail-page--goal-target">Target: {targetPct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Enrollment progress banner */}
        {enrollmentInProgress && (
          <div className="cadence-enrollment-banner">
            <div className="cadence-enrollment-banner--spinner" />
            <span>
              Enrolling recipients&hellip;
              {polledJob.totalItems > 0 &&
                ` (${(polledJob.processedItems || 0).toLocaleString()} of ${polledJob.totalItems.toLocaleString()})`}
            </span>
          </div>
        )}
        {jobFailed && polledJob && (
          <div className="cadence-enrollment-banner is-error">
            <Icon icon="error-outline" />
            <span>Enrollment failed{polledJob.errorMessage ? `: ${polledJob.errorMessage}` : ''}</span>
          </div>
        )}

        {message?.audiences?.length > 0 && (
          <CadenceAudienceSources
            audiences={message.audiences}
            onDisconnect={async (audienceId) => {
              await deleteCadenceAudience(audienceId);
              refetch();
            }}
            canManage={isCadenceOwner}
          />
        )}

        {/* Stats Cards — horizontally scrollable with right-edge fade */}
        <div className="bulk-message-stats-cards-wrap">
          <div className="bulk-message-stats-cards-scroll" onScroll={handleStatsScroll}>
            <div className="bulk-message-stats-cards">
              {statsLoading ? (
                ['Recipients', 'Skipped', 'Sent', 'Delivery Rate', 'Open Rate'].map((label) => (
                  <div key={label} className="bulk-message-stats-cards--card bulk-message-stats-cards--card-loading">
                    <span className="bulk-message-stats-cards--label">{label}</span>
                    <span className="bulk-message-stats-cards--value">&mdash;</span>
                  </div>
                ))
              ) : statsError ? (
                <div className="bulk-message-stats-cards--card">
                  <span className="bulk-message-stats-cards--label">Stats unavailable</span>
                  <span className="bulk-message-stats-cards--value">&mdash;</span>
                </div>
              ) : stats ? (
                <>
                  {[
                    {
                      label: 'Recipients',
                      value: (stats.totalMembers ?? 0).toLocaleString(),
                      tip: 'All constituents added to the bulk message.',
                      calc: 'Recipients = Skipped + Sent',
                    },
                    {
                      label: 'Skipped',
                      value: (() => {
                        if (['Scheduled', 'Draft'].includes(message.displayStatusLabel)) return '0';
                        // Use step-level totalSkipped from backend (accurate: only pre-send validation failures)
                        const bulkStepSkipped = (stepStats || []).reduce((sum, s) => sum + (s.totalSkipped ?? 0), 0);
                        return bulkStepSkipped.toLocaleString();
                      })(),
                      tip: 'Constituents for whom a send was not attempted. Reasons include: past bounce history, previously unsubscribed email, missing contact info (no email on file), or a missing merge field. View the Recipients table below for details.',
                    },
                    {
                      label: 'Sent',
                      value: (stats.emailsSent ?? 0).toLocaleString(),
                      tip: 'Total constituents a send was attempted for. Does not include skipped contacts.',
                    },
                    {
                      label: 'Delivery Rate',
                      value: `${stats.deliveryRate ?? 0}%`,
                      tip: 'Delivered emails are those that successfully reached a constituent\u2019s inbox.',
                      calc: 'Calculation: # Delivered \u00f7 # Sent',
                    },
                    {
                      label: 'Open Rate',
                      value: `${stats.openRate ?? 0}%`,
                      calc: 'Calculation: # unique constituents who opened \u00f7 # unique contacts delivered to',
                    },
                    {
                      label: 'Click Rate',
                      value: `${stats.clickRate ?? 0}%`,
                      calc: 'Calculation: # unique constituents who clicked \u00f7 # unique contacts delivered to',
                    },
                    {
                      label: 'Reply Rate',
                      value: `${stats.replyRate ?? 0}%`,
                      calc: 'Calculation: # unique constituents who replied \u00f7 # unique contacts delivered to',
                    },
                    {
                      label: 'Error Rate',
                      value: `${stats.errorRate ?? 0}%`,
                      tip: 'Emails we attempted to send that did not reach the recipient\u2019s inbox, including sends that failed before they went out. For details, view the Recipients table below.',
                      calc: 'Calculation: # errors \u00f7 # send attempts',
                    },
                    {
                      label: 'Unsubscribe Rate',
                      value: `${stats.unsubscribeRate ?? 0}%`,
                      tip: 'Emails sent in this bulk message that reached the recipient\u2019s inbox and resulted in the recipient unsubscribing.',
                      calc: 'Calculation: # unsubscribed \u00f7 # sent',
                    },
                  ].map(({ label, value, tip, calc }) => (
                    <div key={label} className="bulk-message-stats-cards--card">
                      <span className="bulk-message-stats-cards--label">
                        {label}
                        <ButtonTooltip
                          contentWidth={280}
                          trigger={
                            <span className="bulk-message-stats-cards--info-trigger">
                              <Icon icon="info" className="bulk-message-stats-cards--info-icon" />
                            </span>
                          }
                        >
                          <div className="bulk-message-stats-cards--tooltip">
                            <strong className="bulk-message-stats-cards--tooltip-label">{label}</strong>
                            {tip && <p className="bulk-message-stats-cards--tooltip-tip">{tip}</p>}
                            {calc && <p className="bulk-message-stats-cards--tooltip-calc">{calc}</p>}
                          </div>
                        </ButtonTooltip>
                      </span>
                      <span className="bulk-message-stats-cards--value">{value}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="bulk-message-stats-cards--card">
                  <span className="bulk-message-stats-cards--label">No stats available yet</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {metricProgressList.length > 0 && (
          <div className="bulk-message-refresh-metrics-row">
            <Button type="simple" onClick={handleRefreshMetrics} disabled={refreshingMetrics}>
              <Icon icon="sync" />
              <span>{refreshingMetrics ? 'Refreshing...' : 'Refresh Metrics'}</span>
            </Button>
          </div>
        )}

        {/* Performance and Recipients */}
        <div className="bulk-message-detail-page--combined-card">
          <CadencePerformanceChart cadenceId={messageId} variant="bulk" />
          <div className="bulk-message-detail-page--recipients-section">
            <CadenceRecipientsTable
              cadenceId={messageId}
              variant="bulk"
              cadenceStatus={(message.displayStatus || message.displayStatusLabel || '').toLowerCase()}
              refreshKey={membersRefreshCount}
              onMembersChanged={() => {
                refetch();
                refetchStats();
              }}
            />
          </div>
        </div>
      </div>

      {/* Contact Picker Modal */}
      <Modal isOpen={actions.showContactPicker} closeModal={() => actions.setShowContactPicker(false)}>
        <ContactPicker
          header="Add Recipients to Bulk Message"
          description="Search for contacts to add to this bulk message"
          onSelect={async (contacts) => {
            await actions.handleAddPeople(contacts);
            refetchStats();
            setMembersRefreshCount((c) => c + 1);
          }}
          requestHide={() => actions.setShowContactPicker(false)}
        />
      </Modal>

      <DuplicateCadenceModal
        isOpen={actions.showDuplicateModal}
        onClose={() => actions.setShowDuplicateModal(false)}
        onDuplicate={actions.handleDuplicateConfirm}
        entityName={message?.name}
        entityType="bulk-message"
      />

      <DuplicateCadenceModal
        isOpen={actions.showBlueprintModal}
        onClose={() => actions.setShowBlueprintModal(false)}
        onDuplicate={actions.handleSaveAsBlueprintConfirm}
        entityName={message?.name}
        entityType="blueprint"
      />

      <ConfirmModal
        isOpen={showCancelSendModal}
        onClose={() => setShowCancelSendModal(false)}
        title="Cancel Send?"
        body={buildCancelSendConfirmBody(message?.name)}
        confirmLabel="Cancel Send"
        cancelLabel="Keep Scheduled"
        isDanger
        onConfirm={handleCancelSend}
      />

      <CadenceSharingModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          refetch();
        }}
        cadenceId={messageId}
        cadenceVersion={message?.version}
        cadenceName={message?.name}
        ownerId={message?.ownerUserId}
      />
    </div>
  );
};

BulkMessageDetailPage.propTypes = {
  messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BulkMessageDetailPage;
